import 'emoji-mart/css/emoji-mart.css'
import PropTypes from 'prop-types'
import { Picker } from 'emoji-mart'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { useTheme, styled } from '@mui/material/styles'
import { IconButton } from '@mui/material'
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon'
import { createPortal } from 'react-dom'

const RootStyle = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
  alignItems: 'center',
})

const PickerWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1400,
  width: 325,
  minWidth: 325,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[8],
  borderRadius: 10,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  '& .emoji-mart': {
    width: '100% !important',
    border: 'none',
    backgroundColor: theme.palette.background.paper,
  },
  '& .emoji-mart-bar': { borderColor: theme.palette.divider },
  '& .emoji-mart-scroll': { height: 280 },
}))

export default function EmojiPickerChatSignWise({
  disabled,
  value,
  setValue,
  onEmojiSelect,
  alignRight = false,
  iconButtonSx,
}) {
  const [emojiPickerState, setEmojiPickerState] = useState(false)
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 })
  const rootRef = useRef(null)
  const theme = useTheme()

  const updatePickerPosition = () => {
    const node = rootRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    const width = 325
    const height = 360
    const gap = 8
    let left = alignRight ? rect.right - width : rect.left
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    let top = rect.top - height - gap
    if (top < 8) {
      top = rect.bottom + gap
    }
    setPickerPos({ top, left })
  }

  useLayoutEffect(() => {
    if (!emojiPickerState) return undefined
    updatePickerPosition()
    const onReposition = () => updatePickerPosition()
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [emojiPickerState, alignRight])

  useEffect(() => {
    if (!emojiPickerState) return undefined
    const onPointerDown = (event) => {
      const target = event.target
      if (rootRef.current?.contains(target)) return
      if (target?.closest?.('[data-emoji-picker-panel]')) return
      setEmojiPickerState(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [emojiPickerState])

  const triggerPicker = (e) => {
    e.preventDefault()
    setEmojiPickerState(!emojiPickerState)
  }

  const picker = emojiPickerState
    ? createPortal(
        <PickerWrapper
          data-emoji-picker-panel
          sx={{ top: pickerPos.top, left: pickerPos.left }}
        >
          <Picker
            set="apple"
            title=""
            emoji="point_up"
            emojiTooltip={true}
            showPreview={false}
            perLine={8}
            sheetSize={32}
            showSkinTones={false}
            theme="light"
            color={theme.palette.primary.main}
            onSelect={(emoji) => {
              let native = emoji.native
              if (emoji.unified.includes('-')) {
                native = emoji.unified
                  .split('-')
                  .map((code) => String.fromCodePoint(parseInt(code, 16)))
                  .join('')
              }
              if (typeof onEmojiSelect === 'function') {
                onEmojiSelect(native)
                return
              }
              if (typeof setValue === 'function') {
                setValue(String(value || '') + native)
              }
            }}
            emojiSize={28}
            style={{
              backgroundColor: theme.palette.background.paper,
              border: 'none',
              width: '100%',
            }}
          />
        </PickerWrapper>,
        document.body,
      )
    : null

  return (
    <RootStyle ref={rootRef}>
      {picker}
      <IconButton
        disabled={disabled}
        size="small"
        onClick={triggerPicker}
        sx={{ color: '#637381', flexShrink: 0, ...(iconButtonSx || {}) }}
      >
        <InsertEmoticonIcon fontSize="small" />
      </IconButton>
    </RootStyle>
  )
}

EmojiPickerChatSignWise.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.string,
  setValue: PropTypes.func,
  onEmojiSelect: PropTypes.func,
  alignRight: PropTypes.bool,
  iconButtonSx: PropTypes.object,
}
